// ============================================================
//  CATEGORY COMMUNICATION PAGE — ADMIN DASHBOARD
//  Path: src/pages/CategoryCommunicationPage.tsx
//
//  Per-category control over all three communication channels:
//  voice calls, video calls, and chat — each independently
//  togglable for pre-hire (shortlisted) and post-hire
//  (assigned/pending_completion) stages.
//
//  Admin can also set per-stage duration caps (how long a single
//  call can run) and call-count caps (how many calls are allowed
//  per application/task). All fields are protected by
//  adminOverriddenFields on the backend — changing anything here
//  is never overwritten by a future seed deploy.
//
//  Default state (before any admin touches a category):
//    Chat:            ON  — on for every category; admin kill-switch here
//    Pre-hire voice:  OFF — admin turns on per category as needed
//    Pre-hire video:  OFF — admin turns on per category as needed
//    Post-hire voice: OFF — admin turns on per category as needed
//    Post-hire video: OFF — admin turns on per category as needed
//  Call caps left blank (every stage, voice and video): 3 minutes per
//  call, 1 call. The same defaults live in the backend (schema, seed.js
//  and the call service), which is what actually enforces them — this
//  page only states them.
//
//  Important: post-hire calls are gated by category flags ONLY.
//  Expert and customer personal toggles (in their Settings screens)
//  only affect pre-hire interactions — once hired, both sides have
//  committed and communication channels stay open regardless of
//  personal preferences. The admin category toggle is the sole
//  post-hire gate.
//
//  Access:
//    super_admin, admin  → full access (can toggle everything)
//    customer_service    → view only
// ============================================================

import React, { useEffect, useState, useCallback } from 'react'
import { listAllCategoriesAdmin, updateCategory, getCategoryGroupOrder } from '../services/admin.service'
import { useAuth } from '../context/AuthContext'

// ── Types ─────────────────────────────────────────────────────

interface CommCategory {
  id:   string
  name: string
  slug: string
  group: string | null
  isActive: boolean
  // Chat
  chatEnabled: boolean
  // Pre-hire
  voiceCallPreHireEnabled:      boolean
  videoCallPreHireEnabled:      boolean
  voiceCallPreHireLimitMinutes: number | null
  videoCallPreHireLimitMinutes: number | null
  voiceCallPreHireMaxCalls:     number | null
  videoCallPreHireMaxCalls:     number | null
  // Post-hire
  voiceCallPostHireEnabled:      boolean
  videoCallPostHireEnabled:      boolean
  voiceCallPostHireLimitMinutes: number | null
  videoCallPostHireLimitMinutes: number | null
  voiceCallPostHireMaxCalls:     number | null
  videoCallPostHireMaxCalls:     number | null
  // Legacy shared fallback
  callTimeLimitMinutes: number | null
}

type TabKey = 'chat' | 'voice' | 'video'

const sortGroupNames = (groups: string[], order: string[]): string[] =>
  [...groups].sort((a, b) => {
    const ai = order.indexOf(a), bi = order.indexOf(b)
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })

// Default caps when a field is left blank — must match the backend.
const DEFAULT_CALL_LIMIT_MINUTES = 3
const DEFAULT_MAX_CALLS          = 1
const DEFAULT_CAPS_HINT = `Blank limit = ${DEFAULT_CALL_LIMIT_MINUTES} min default. Blank max = ${DEFAULT_MAX_CALLS} call.`

// ── Small reusable toggle ─────────────────────────────────────

const Toggle: React.FC<{
  checked:  boolean
  onChange: () => void
  disabled: boolean
  color?:   string
}> = ({ checked, onChange, disabled, color = 'bg-brand-500' }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={disabled}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${
      checked ? color : 'bg-gray-600'
    }`}
  >
    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
      checked ? 'translate-x-4' : 'translate-x-0.5'
    }`} />
  </button>
)

// ── Numeric cap input (limit minutes / max calls) ─────────────

const CapInput: React.FC<{
  value:       number | null
  placeholder: string
  disabled:    boolean
  onCommit:    (val: number | null) => void
}> = ({ value, placeholder, disabled, onCommit }) => {
  const [draft, setDraft] = useState<string>('')
  const [editing, setEditing] = useState(false)

  const display = editing ? draft : (value !== null ? String(value) : '')

  const handleBlur = () => {
    setEditing(false)
    const trimmed = draft.trim()
    if (trimmed === '') { onCommit(null); return }
    const n = Number(trimmed)
    if (Number.isInteger(n) && n >= 1) onCommit(n)
    else setDraft(value !== null ? String(value) : '')
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      placeholder={placeholder}
      value={display}
      disabled={disabled}
      onFocus={() => { setEditing(true); setDraft(value !== null ? String(value) : '') }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
      className="w-14 bg-surface-tertiary border border-border-light rounded-lg px-2 py-1 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-brand-500 disabled:opacity-40 disabled:cursor-not-allowed text-center"
    />
  )
}

// ── Per-channel row inside the expanded panel ─────────────────

const ChannelRow: React.FC<{
  iconOn:      string
  iconOff:     string
  label:       string
  enabled:     boolean
  limitMins:   number | null
  maxCalls:    number | null
  canModify:   boolean
  saving:      boolean
  onToggle:    () => void
  onLimitBlur: (val: number | null) => void
  onMaxBlur:   (val: number | null) => void
  showCaps:    boolean  // false for chat — no duration/count concept
  limitPlaceholder?: string
  maxPlaceholder?:   string
  color?:            string
}> = ({
  iconOn, iconOff, label, enabled, limitMins, maxCalls,
  canModify, saving, onToggle, onLimitBlur, onMaxBlur,
  showCaps, limitPlaceholder = 'min', maxPlaceholder = 'calls', color,
}) => (
  <div className={`flex items-center gap-4 py-2.5 px-3 rounded-xl transition-colors ${
    enabled ? 'bg-surface-tertiary/60' : 'bg-transparent opacity-60'
  }`}>
    <span className="text-base w-5 text-center">{enabled ? iconOn : iconOff}</span>
    <span className="text-sm text-gray-300 w-28 shrink-0">{label}</span>

    {canModify ? (
      <Toggle checked={enabled} onChange={onToggle} disabled={saving} color={color} />
    ) : (
      <span className={`px-2 py-0.5 text-xs rounded-full ${
        enabled ? 'bg-green-600/20 text-green-400' : 'bg-surface-tertiary text-gray-500'
      }`}>
        {enabled ? 'On' : 'Off'}
      </span>
    )}

    {showCaps && (
      <>
        <div className="flex items-center gap-1.5 ml-4">
          <span className="text-xs text-gray-500">Limit:</span>
          {canModify ? (
            <CapInput
              value={limitMins}
              placeholder={limitPlaceholder}
              disabled={saving || !enabled}
              onCommit={onLimitBlur}
            />
          ) : (
            <span className="text-xs text-gray-400">
              {limitMins !== null ? `${limitMins} min` : `${DEFAULT_CALL_LIMIT_MINUTES} min (default)`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">Max calls:</span>
          {canModify ? (
            <CapInput
              value={maxCalls}
              placeholder={maxPlaceholder}
              disabled={saving || !enabled}
              onCommit={onMaxBlur}
            />
          ) : (
            <span className="text-xs text-gray-400">
              {maxCalls !== null ? maxCalls : `${DEFAULT_MAX_CALLS} (default)`}
            </span>
          )}
        </div>
      </>
    )}
  </div>
)

// ── Pre-hire / post-hire grouping used inside the Voice and Video tabs ─

const StageSection: React.FC<{
  dot:         string   // tailwind bg color for the stage dot
  title:       string
  description: string
  children:    React.ReactNode
}> = ({ dot, title, description, children }) => (
  <div className="bg-surface-tertiary/30 border border-border-light rounded-xl p-4">
    <div className="flex items-center gap-2 mb-1">
      <span className={`w-2 h-2 rounded-full ${dot} inline-block`} />
      <span className="text-xs font-semibold text-white uppercase tracking-wider">{title}</span>
    </div>
    <p className="text-xs text-gray-500 mb-3">{description}</p>
    {children}
  </div>
)

// Fields this page can send to updateCategory — a strict subset
// of the full updateCategory type, so TypeScript catches any
// typo or drift between CommCategory and what the API accepts.
type CommPatch = {
  chatEnabled?: boolean
  voiceCallPreHireEnabled?:      boolean
  videoCallPreHireEnabled?:      boolean
  voiceCallPostHireEnabled?:     boolean
  videoCallPostHireEnabled?:     boolean
  voiceCallPreHireLimitMinutes?:  number | null
  videoCallPreHireLimitMinutes?:  number | null
  voiceCallPostHireLimitMinutes?: number | null
  videoCallPostHireLimitMinutes?: number | null
  voiceCallPreHireMaxCalls?:     number | null
  videoCallPreHireMaxCalls?:     number | null
  voiceCallPostHireMaxCalls?:    number | null
  videoCallPostHireMaxCalls?:    number | null
}

const CategoryCommunicationPage: React.FC = () => {
  const { user } = useAuth()
  const canModify = user?.roles.includes('super_admin') || user?.roles.includes('admin')

  const [categories, setCategories] = useState<CommCategory[]>([])
  const [groupOrder, setGroupOrder] = useState<string[]>([])
  const [loading,    setLoading]    = useState(true)
  const [search,     setSearch]     = useState('')
  const [expanded,   setExpanded]   = useState<Set<string>>(new Set())
  // Which of the three tabs (chat / voice / video) is showing per expanded category
  const [activeTab,  setActiveTab]  = useState<Record<string, TabKey>>({})
  // savingId tracks which category is currently mid-save
  const [savingId,   setSavingId]   = useState<string | null>(null)
  const [saveError,  setSaveError]  = useState<string | null>(null)

  // ── Data fetching ───────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [catRes, groupRes] = await Promise.all([
        listAllCategoriesAdmin(),
        getCategoryGroupOrder(),
      ])
      setCategories(catRes.data.data)
      setGroupOrder(groupRes.data.data)
    } catch (err) {
      
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // ── Generic save helper ─────────────────────────────────────
  // Optimistically updates local state, fires the API, reverts
  // on failure. Same pattern as every handler in CategoriesPage.

  const save = async (categoryId: string, patch: CommPatch) => {
    if (!canModify) return
    setSavingId(categoryId)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, ...patch } : c))
    try {
      await updateCategory(categoryId, patch)
    } catch (err: any) {
      
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchAll()
    } finally {
      setSavingId(null)
    }
  }

  // ── Toggle helper ───────────────────────────────────────────

  const toggle = (category: CommCategory, field: keyof CommPatch) =>
    save(category.id, { [field]: !category[field] })

  // ── Filtering and grouping ──────────────────────────────────

  const filtered = categories.filter((c) =>
    !search.trim() ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.group || '').toLowerCase().includes(search.toLowerCase())
  )

  const grouped = filtered.reduce((acc, c) => {
    const key = c.group || 'Ungrouped'
    if (!acc[key]) acc[key] = []
    acc[key].push(c)
    return acc
  }, {} as Record<string, CommCategory[]>)

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
        setActiveTab((tabs) => (tabs[id] ? tabs : { ...tabs, [id]: 'chat' }))
      }
      return next
    })

  const closeCategory = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })

  const getTab = (id: string): TabKey => activeTab[id] ?? 'chat'
  const setTab = (id: string, tab: TabKey) => setActiveTab((prev) => ({ ...prev, [id]: tab }))

  // ── Summary badge for collapsed row ────────────────────────

  const summaryBadges = (c: CommCategory) => {
    const badges: { label: string; color: string }[] = []
    if (c.chatEnabled)              badges.push({ label: '💬 Chat', color: 'bg-blue-600/20 text-blue-400' })
    if (c.voiceCallPreHireEnabled)  badges.push({ label: '📞 Pre-hire voice', color: 'bg-green-600/20 text-green-400' })
    if (c.videoCallPreHireEnabled)  badges.push({ label: '📹 Pre-hire video', color: 'bg-green-600/20 text-green-400' })
    if (c.voiceCallPostHireEnabled) badges.push({ label: '📞 Post-hire voice', color: 'bg-purple-600/20 text-purple-400' })
    if (c.videoCallPostHireEnabled) badges.push({ label: '📹 Post-hire video', color: 'bg-purple-600/20 text-purple-400' })
    if (badges.length === 0)        badges.push({ label: 'All off', color: 'bg-surface-tertiary text-gray-500' })
    return badges
  }

  // ── Render ──────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Communication Settings</h1>
          <p className="text-gray-400 text-sm mt-1">
            Control voice, video, and chat per category — pre-hire and post-hire independently
          </p>
        </div>
        {!canModify && (
          <span className="px-3 py-1.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400">View Only</span>
        )}
      </div>

      {/* Legend */}
      <div className="bg-surface-card border border-border-light rounded-2xl p-4 mb-6 flex flex-wrap gap-4 text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
          Pre-hire — available when an expert is shortlisted (before hiring)
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-purple-500 inline-block" />
          Post-hire — available once the expert is hired (assigned / pending completion)
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" />
          Chat — applies across all stages (shortlisted through completed)
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or group..."
        className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-6"
      />

      {/* Error banner */}
      {saveError && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 mb-6 flex items-center justify-between">
          <p className="text-sm text-red-400">{saveError}</p>
          <button onClick={() => setSaveError(null)} className="text-red-400 hover:text-white text-sm">✕</button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No categories match your search</div>
      ) : (
        sortGroupNames(Object.keys(grouped), groupOrder).map((groupName) => (
          <div key={groupName} className="mb-8">
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">{groupName}</h2>
            <div className="space-y-3">
              {grouped[groupName].map((category) => {
                const isExpanded = expanded.has(category.id)
                const isSaving   = savingId === category.id
                const badges     = summaryBadges(category)
                const tab        = getTab(category.id)

                return (
                  <div
                    key={category.id}
                    className={`bg-surface-card border rounded-2xl overflow-hidden transition-colors ${
                      isExpanded ? 'border-brand-500/40' : 'border-border-light'
                    }`}
                  >
                    {/* ── Collapsed row ── */}
                    <button
                      type="button"
                      onClick={() => toggleExpanded(category.id)}
                      className="w-full flex items-center gap-4 px-5 py-4 hover:bg-surface-tertiary/40 transition-colors text-left"
                    >
                      {/* Chevron */}
                      <span className={`text-gray-500 text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>

                      {/* Name + active badge */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white truncate">{category.name}</span>
                          {!category.isActive && (
                            <span className="px-1.5 py-0.5 text-xs rounded-full bg-red-600/20 text-red-400 shrink-0">Inactive</span>
                          )}
                        </div>
                        {/* Summary badges */}
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {badges.map((b) => (
                            <span key={b.label} className={`px-2 py-0.5 text-xs rounded-full ${b.color}`}>{b.label}</span>
                          ))}
                        </div>
                      </div>

                      {isSaving && (
                        <span className="text-xs text-gray-500 shrink-0 animate-pulse">Saving…</span>
                      )}
                    </button>

                    {/* ── Expanded panel ── */}
                    {isExpanded && (
                      <div className="border-t border-border-light bg-surface-tertiary/20">
                        {/* Tab bar */}
                        <div className="flex items-center justify-between px-5 pt-3 gap-3">
                          <div className="flex items-center gap-2">
                            {([
                              { key: 'chat',  iconOn: '💬', iconOff: '🔕', label: 'Chat',  active: category.chatEnabled },
                              { key: 'voice', iconOn: '📞', iconOff: '📵', label: 'Voice', active: category.voiceCallPreHireEnabled || category.voiceCallPostHireEnabled },
                              { key: 'video', iconOn: '📹', iconOff: '🚫', label: 'Video', active: category.videoCallPreHireEnabled || category.videoCallPostHireEnabled },
                            ] as { key: TabKey; iconOn: string; iconOff: string; label: string; active: boolean }[]).map((t) => (
                              <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(category.id, t.key)}
                                aria-pressed={tab === t.key}
                                className={`relative flex items-center gap-1.5 px-4 py-2 rounded-t-lg text-sm transition-all ${
                                  tab === t.key
                                    ? 'bg-white text-gray-900 font-bold shadow-lg'
                                    : 'bg-surface-tertiary/40 text-gray-400 font-medium hover:text-gray-200 hover:bg-surface-tertiary/70'
                                }`}
                              >
                                <span>{t.active ? t.iconOn : t.iconOff}</span>
                                {t.label}
                                {tab === t.key && (
                                  <span className="absolute -bottom-[1px] left-0 right-0 h-[3px] bg-brand-500 rounded-full" />
                                )}
                              </button>
                            ))}
                          </div>

                          {/* Close */}
                          <button
                            type="button"
                            onClick={() => closeCategory(category.id)}
                            className="text-gray-500 hover:text-white text-sm px-2 py-1 rounded-lg hover:bg-surface-tertiary transition-colors shrink-0"
                            aria-label="Close"
                          >
                            ✕ Close
                          </button>
                        </div>

                        {/* Tab content */}
                        <div className="bg-surface-card border-t border-border-light px-5 py-5">

                          {tab === 'chat' && (
                            <div className="max-w-md">
                              <p className="text-xs text-gray-500 mb-3">
                                Applies across all stages — shortlisted, hired, and post-completion grace window.
                              </p>
                              <ChannelRow
                                iconOn="💬"
                                iconOff="🔕"
                                label="Chat enabled"
                                enabled={category.chatEnabled}
                                limitMins={null}
                                maxCalls={null}
                                canModify={canModify ?? false}
                                saving={isSaving}
                                onToggle={() => toggle(category, 'chatEnabled')}
                                onLimitBlur={() => {}}
                                onMaxBlur={() => {}}
                                showCaps={false}
                                color="bg-blue-500"
                              />
                            </div>
                          )}

                          {tab === 'voice' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <StageSection
                                dot="bg-green-500"
                                title="Pre-hire"
                                description="Available once an expert is shortlisted. Off by default — turn on per category."
                              >
                                <ChannelRow
                                  iconOn="📞"
                                  iconOff="📵"
                                  label="Voice call"
                                  enabled={category.voiceCallPreHireEnabled}
                                  limitMins={category.voiceCallPreHireLimitMinutes}
                                  maxCalls={category.voiceCallPreHireMaxCalls}
                                  canModify={canModify ?? false}
                                  saving={isSaving}
                                  onToggle={() => toggle(category, 'voiceCallPreHireEnabled')}
                                  onLimitBlur={(v) => save(category.id, { voiceCallPreHireLimitMinutes: v })}
                                  onMaxBlur={(v) => save(category.id, { voiceCallPreHireMaxCalls: v })}
                                  showCaps
                                  limitPlaceholder={String(DEFAULT_CALL_LIMIT_MINUTES)}
                                  maxPlaceholder={String(DEFAULT_MAX_CALLS)}
                                  color="bg-green-500"
                                />
                                <p className="text-xs text-gray-600 mt-2">
                                  {DEFAULT_CAPS_HINT}
                                </p>
                              </StageSection>

                              <StageSection
                                dot="bg-purple-500"
                                title="Post-hire"
                                description="Available once hired, within the scheduled time window. Off by default — enable per category. This is the only gate for post-hire calls — expert and customer personal toggles do not apply once hired."
                              >
                                <ChannelRow
                                  iconOn="📞"
                                  iconOff="📵"
                                  label="Voice call"
                                  enabled={category.voiceCallPostHireEnabled}
                                  limitMins={category.voiceCallPostHireLimitMinutes}
                                  maxCalls={category.voiceCallPostHireMaxCalls}
                                  canModify={canModify ?? false}
                                  saving={isSaving}
                                  onToggle={() => toggle(category, 'voiceCallPostHireEnabled')}
                                  onLimitBlur={(v) => save(category.id, { voiceCallPostHireLimitMinutes: v })}
                                  onMaxBlur={(v) => save(category.id, { voiceCallPostHireMaxCalls: v })}
                                  showCaps
                                  limitPlaceholder={String(DEFAULT_CALL_LIMIT_MINUTES)}
                                  maxPlaceholder={String(DEFAULT_MAX_CALLS)}
                                  color="bg-purple-500"
                                />
                                <p className="text-xs text-gray-600 mt-2">
                                  {DEFAULT_CAPS_HINT}
                                </p>
                              </StageSection>
                            </div>
                          )}

                          {tab === 'video' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <StageSection
                                dot="bg-green-500"
                                title="Pre-hire"
                                description="Available once an expert is shortlisted. Off by default — turn on per category."
                              >
                                <ChannelRow
                                  iconOn="📹"
                                  iconOff="🚫"
                                  label="Video call"
                                  enabled={category.videoCallPreHireEnabled}
                                  limitMins={category.videoCallPreHireLimitMinutes}
                                  maxCalls={category.videoCallPreHireMaxCalls}
                                  canModify={canModify ?? false}
                                  saving={isSaving}
                                  onToggle={() => toggle(category, 'videoCallPreHireEnabled')}
                                  onLimitBlur={(v) => save(category.id, { videoCallPreHireLimitMinutes: v })}
                                  onMaxBlur={(v) => save(category.id, { videoCallPreHireMaxCalls: v })}
                                  showCaps
                                  limitPlaceholder={String(DEFAULT_CALL_LIMIT_MINUTES)}
                                  maxPlaceholder={String(DEFAULT_MAX_CALLS)}
                                  color="bg-green-500"
                                />
                                <p className="text-xs text-gray-600 mt-2">
                                  {DEFAULT_CAPS_HINT}
                                </p>
                              </StageSection>

                              <StageSection
                                dot="bg-purple-500"
                                title="Post-hire"
                                description="Available once hired, within the scheduled time window. Off by default — enable per category. This is the only gate for post-hire calls — expert and customer personal toggles do not apply once hired."
                              >
                                <ChannelRow
                                  iconOn="📹"
                                  iconOff="🚫"
                                  label="Video call"
                                  enabled={category.videoCallPostHireEnabled}
                                  limitMins={category.videoCallPostHireLimitMinutes}
                                  maxCalls={category.videoCallPostHireMaxCalls}
                                  canModify={canModify ?? false}
                                  saving={isSaving}
                                  onToggle={() => toggle(category, 'videoCallPostHireEnabled')}
                                  onLimitBlur={(v) => save(category.id, { videoCallPostHireLimitMinutes: v })}
                                  onMaxBlur={(v) => save(category.id, { videoCallPostHireMaxCalls: v })}
                                  showCaps
                                  limitPlaceholder={String(DEFAULT_CALL_LIMIT_MINUTES)}
                                  maxPlaceholder={String(DEFAULT_MAX_CALLS)}
                                  color="bg-purple-500"
                                />
                                <p className="text-xs text-gray-600 mt-2">
                                  {DEFAULT_CAPS_HINT}
                                </p>
                              </StageSection>
                            </div>
                          )}

                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

export default CategoryCommunicationPage